import React, { useEffect, useMemo, useState } from 'react'
import { Autocomplete } from '@aws-amplify/ui-react'
import Box from '@components/base/Box'
import { useTheme } from '@libs/useTheme'
import { BaseInputProps } from './BaseInputProps'
import { validate } from './Validations'
import { DropdownOptions } from './DropdownInput'
import { isBasicOptions } from '@libs/Utility/util'

export type BasicOptions = { id: string; label: string }

export interface SearchInputProps extends BaseInputProps {
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  border?: 'border' | 'border-none'
  max?: number
  min?: number
  options: BasicOptions[] | DropdownOptions[]
  addValueInOptions?: boolean // optional boolean prop
}

export const SearchInput = ({
  label,
  placeholder,
  value = '',
  onChange,
  className,
  hint,
  validations,
  required,
  border = 'border',
  onKeyDown,
  max,
  min,
  options,
  addValueInOptions = false, // default to false
  textColour,
  labelBgColour,
  variation = 'v1',
}: SearchInputProps) => {
  const { theme } = useTheme()

  const backgroundColour = labelBgColour ?? theme?.backgroundColour ?? '#0D0E11'

  const [autocompleteValue, setAutocompleteValue] = useState(value)

  useEffect(() => {
    setAutocompleteValue(value)
  }, [value])

  const errorMessage = validate(value, validations)

  const generateStyles = useMemo(() => {
    let base = `transition-all outline-none `

    const errorStyles = errorMessage ? 'text-red-500 border-red-500' : ''
    let textStyles = 'text-sm placeholder-gray-500'
    let positionStyles = ''

    if (variation === 'v1') {
      base = `bg-transparent rounded-2xl`
    }

    if (variation === 'v2') {
      base = `!bg-[#F7E5E4] rounded-none !border-none`
      textStyles = 'text-sm text-black black-autocomplete-text font-semibold'
      border = 'border-none'
    }

    const payload = `${base} ${textStyles} ${positionStyles} ${border} ${errorStyles}`
    console.log({ payload })
    return payload
  }, [border, errorMessage, variation])

  const getOptions = (): BasicOptions[] => {
    if (!isBasicOptions(options))
      options = options.map((o) => ({
        id: o?.value.toString(),
        label: o?.label,
      }))

    const isValueInOptions = options.some(
      (option) => option.label === autocompleteValue
    )

    const modifiedOptions =
      addValueInOptions && !isValueInOptions
        ? [{ id: value, label: value }, ...options]
        : options

    return modifiedOptions
  }

  const input = (
    <Autocomplete
      label={label}
      clearButtonLabel={label ? 'Clear ' + label : 'Clear search'}
      onClear={() => setAutocompleteValue('')}
      required={required}
      onKeyDown={onKeyDown}
      value={autocompleteValue}
      onChange={(e) => {
        setAutocompleteValue(e.target.value)
        const isInvalid = validate(e.target.value, validations) !== ''
        onChange && onChange(e.target.value, isInvalid)
      }}
      onSelect={(e) => {
        setAutocompleteValue(e.label)
        const isInvalid = validate(e.id, validations) !== ''
        onChange && onChange(e.id, isInvalid)
      }}
      max={max}
      min={min}
      placeholder={placeholder}
      hasSearchIcon={false}
      className={generateStyles}
      color={variation === 'v1' ? 'grey' : 'black'}
      inputStyles={{
        padding: '15.5px 16px',
        fontSize: '14px',
        color: 'black',
        ...(variation === 'v2' ? { border: 'none', color: '#000000' } : {}),
        ...(variation === 'v1' ? { borderRadius: '1rem' } : {}),
      }}
      options={getOptions()}
      crossOrigin={undefined}
      onPointerEnterCapture={undefined}
      onPointerOutCapture={undefined}
      onPointerLeaveCapture={undefined}
    />
  )

  const showLabel = useMemo(
    () => label && variation === 'v1',
    [label, variation]
  )

  return (
    <label
      className={['flex flex-col my-4', className].join(' ')}
      style={{ color: textColour }}
    >
      {showLabel && (
        <span
          className={`absolute text-sm mb-2 ml-2 z-10 ${backgroundColour} ${
            required ? 'pl-2' : 'px-2'
          } transition-all ${errorMessage ? 'text-red-500' : ''}`}
          style={{
            background: backgroundColour,
            transform: 'translateY(-50%)',
          }}
        >
          <span
            style={{ backgroundColor: backgroundColour }}
            className={`z-10 ${
              required
                ? "after:content-['*'] after:mx-2 after:text-red-500"
                : ''
            }`}
          >
            {label}
          </span>
        </span>
      )}
      {input}
      <Box
        ignoreTheme
        className={`transition-all ${
          errorMessage ? 'opacity-100' : 'opacity-0'
        } text-red-500 text-sm font-bold`}
      >
        {errorMessage}
      </Box>
    </label>
  )
}
